import { useCallback, useEffect, useRef, useState } from "react";
import { getLocationByAddress } from "../../../actions/googleAPI";

/**
 * Checks a billing postal code against the geocoding endpoint while the user
 * types, and reports what is happening so the form can show it.
 *
 * status:
 *   "idle"     – nothing to check (empty field)
 *   "checking" – request in flight
 *   "found"    – the postal code exists; `city` holds the city name
 *   "notFound" – the endpoint answered, but the postal code does not exist
 *   "failed"   – the request itself failed (offline, server error); call retry()
 *
 * onFound(city) / onNotFound() run once per finished check, so the page can
 * update its form data and error messages.
 */

export const POSTAL_CODE_STATUS = {
    IDLE: "idle",
    CHECKING: "checking",
    FOUND: "found",
    NOT_FOUND: "notFound",
    FAILED: "failed",
};

const TYPING_PAUSE_MS = 500; // wait for the user to stop typing before checking

// getLocationByAddress returns a geocode result object on success, `undefined`
// when nothing matched, and `[]` when the request failed — so tell them apart.
const isRequestFailure = (lookupResult) => Array.isArray(lookupResult);
const isGeocodeResult = (lookupResult) =>
    Boolean(lookupResult) && Array.isArray(lookupResult.address_components);

// Prefer the city / ward; fall back to the component before the country.
const getCityName = (geocodeResult) => {
    const components = geocodeResult.address_components;
    const cityComponent = components.find(
        (component) =>
            component.types.includes("administrative_area_level_2") || component.types.includes("locality")
    );
    return (cityComponent || components[components.length - 2])?.long_name || "";
};

const usePostalCodeLookup = ({ postalCode, countryCode, isEnabled = true, onFound, onNotFound }) => {
    const [status, setStatus] = useState(POSTAL_CODE_STATUS.IDLE);
    const [city, setCity] = useState("");
    const [retryCount, setRetryCount] = useState(0);

    // Latest callbacks, so the effect below doesn't re-run when the page
    // re-renders with new function identities.
    const callbacksRef = useRef({ onFound, onNotFound });
    callbacksRef.current = { onFound, onNotFound };

    useEffect(() => {
        if (!isEnabled || !postalCode || !countryCode) {
            setStatus(POSTAL_CODE_STATUS.IDLE);
            return undefined;
        }

        // Set to true when the user types again (or leaves the page) before this
        // check finishes, so an old answer can never overwrite a newer one.
        let isOutdated = false;

        const typingPauseTimer = setTimeout(async () => {
            setStatus(POSTAL_CODE_STATUS.CHECKING);
            try {
                const lookupResult = await getLocationByAddress(postalCode, countryCode);
                if (isOutdated) return;

                if (isRequestFailure(lookupResult)) {
                    setStatus(POSTAL_CODE_STATUS.FAILED);
                    return;
                }

                if (isGeocodeResult(lookupResult)) {
                    const foundCity = getCityName(lookupResult);
                    setCity(foundCity);
                    setStatus(POSTAL_CODE_STATUS.FOUND);
                    callbacksRef.current.onFound?.(foundCity);
                } else {
                    setStatus(POSTAL_CODE_STATUS.NOT_FOUND);
                    callbacksRef.current.onNotFound?.();
                }
            } catch (error) {
                if (isOutdated) return;
                console.error("Postal code check failed", { countryCode, error: error?.message });
                setStatus(POSTAL_CODE_STATUS.FAILED);
            }
        }, TYPING_PAUSE_MS);

        return () => {
            isOutdated = true;
            clearTimeout(typingPauseTimer);
        };
    }, [postalCode, countryCode, isEnabled, retryCount]);

    // Runs the same check again (used by the "Try again" link).
    const retry = useCallback(() => setRetryCount((count) => count + 1), []);

    return { status, city, retry };
};

export default usePostalCodeLookup;
